import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMMKVNumber, useMMKVString } from 'react-native-mmkv';

import type { NPSCampaign } from '@/api/surveys/getNPSCampaignQuery';
import { type BottomSheetRef } from '@/components/_shared/BottomSheet';
import { NPSSurveyModal } from '@/components/_shared/NPSSurveyModal';
import { storage, storageKeys } from '@/constants/storage';
import { useAppStoreReviewContext } from '@/context/appStoreReview';
import { useNPSSurveyEligibility } from '@/hooks/customer/useNPSSurveyEligibility';
import { Logger } from '@/utils/logging';

interface NPSSurveyContextType {
  triggerNPSCheck: (customerId: string) => void;
  triggerNPSPreview: () => void;
}

const NPSSurveyContext = createContext<NPSSurveyContextType | undefined>(
  undefined,
);

export interface NPSSurveyProviderProps {
  children: React.ReactNode;
}

const NPS_SNOOZE_MS = 24 * 60 * 60 * 1000; // 24 hours

const MOCK_NPS_CAMPAIGN: NPSCampaign = {
  id: '12345678-1234-1234-1234-123456789abc',
  campaignId: '12345678-1234-1234-1234-123456789abc',
  userCampaignId: '84fbfd45-d04f-4139-b313-7d2358299009',
  status: 'active',
  campaignName: 'Dripping NPS campaign',
  survey: {
    id: '12345678-1234-1234-1234-123456789abc',
    name: 'NPS',
    description:
      'Survey asking users if they would recommend the product to a friend',
    questions: [
      {
        id: '3a670328-3135-40c4-9108-2e2e6586f396',
        question:
          'How likely are you to recommend SQUIRE to your friends and colleagues?',
        type: 'scale_11',
        required: true,
        hints: ['0 = Not at all likely', '10 = Extremely likely'],
      },
      {
        id: '5184c3fa-7921-4244-b089-1e3373d34186',
        question: 'Anything else you want to add?',
        type: 'text',
        required: false,
        hints: [],
      },
    ],
  },
};

export function NPSSurveyProvider({ children }: NPSSurveyProviderProps) {
  // Seed from persisted storage so already-logged-in users are checked on app start.
  const [persistedCustomerId = ''] = useMMKVString(
    storageKeys.customerId,
    storage,
  );
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [npsNextEligibleAt, setNpsNextEligibleAt] = useMMKVNumber(
    storageKeys.npsNextEligibleAt,
    storage,
  );

  const effectiveCustomerId = customerId ?? persistedCustomerId;
  const [activeCampaign, setActiveCampaign] = useState<NPSCampaign | null>(
    null,
  );
  const bottomSheetRef = useRef<BottomSheetRef>(null);

  const { campaign } = useNPSSurveyEligibility(effectiveCustomerId);
  const { reviewCheckStatus } = useAppStoreReviewContext();

  useEffect(() => {
    if (!campaign || activeCampaign) return;

    // Wait for an in-flight review check: the survey must never open alongside
    // the review sheet, whichever of the two resolves first.
    if (reviewCheckStatus === 'checking') return;

    const isSnoozed =
      npsNextEligibleAt !== undefined && Date.now() < npsNextEligibleAt;

    if (reviewCheckStatus === 'prompted') {
      if (!isSnoozed) {
        Logger.console('[NPS] review prompt shown, snoozing survey', {
          campaignId: campaign.id,
        });
        setNpsNextEligibleAt(Date.now() + NPS_SNOOZE_MS);
      }
      return;
    }

    if (isSnoozed) return;

    Logger.console('[NPS] campaign resolved, opening modal', {
      campaignId: campaign.id,
    });
    setActiveCampaign(campaign);
  }, [
    campaign,
    activeCampaign,
    npsNextEligibleAt,
    reviewCheckStatus,
    setNpsNextEligibleAt,
  ]);

  const triggerNPSCheck = useCallback((id: string) => {
    Logger.console('[NPS] triggerNPSCheck called', { customerId: id });
    setCustomerId(id);
  }, []);

  const triggerNPSPreview = useCallback(() => {
    Logger.console('[NPS] triggerNPSPreview called — bypassing eligibility');
    setNpsNextEligibleAt(undefined);
    setActiveCampaign(MOCK_NPS_CAMPAIGN);
  }, [setNpsNextEligibleAt]);

  const handleDismiss = useCallback(() => {
    Logger.console('[NPS] modal dismissed, resetting state');
    setNpsNextEligibleAt(Date.now() + NPS_SNOOZE_MS);
    setCustomerId(null);
    setActiveCampaign(null);
  }, [setNpsNextEligibleAt]);

  const value: NPSSurveyContextType = { triggerNPSCheck, triggerNPSPreview };

  return (
    <NPSSurveyContext.Provider value={value}>
      {children}
      {activeCampaign && (
        <NPSSurveyModal
          ref={bottomSheetRef}
          campaign={activeCampaign}
          userId={effectiveCustomerId}
          onDismiss={handleDismiss}
        />
      )}
    </NPSSurveyContext.Provider>
  );
}

export function useNPSSurveyContext(): NPSSurveyContextType {
  const context = useContext(NPSSurveyContext);
  if (!context) {
    throw new Error(
      'useNPSSurveyContext must be used within NPSSurveyProvider',
    );
  }
  return context;
}
